import { useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';

import { useDeleteBrand, useDeleteDenomination, useDeletionCheck } from '../api/brands.queries';

const REASON_MAX = 500;

/**
 * Deleting a brand — only when none of its denominations is used by an order,
 * cart, payment or customer. Everything else goes with it: its denominations,
 * their country offerings and provider mappings. The check is fetched when the
 * dialog opens (so it works from the list, which does not carry it) and
 * repeated by the Admin API under a lock.
 */
export function DeleteBrandDialog({
  brand,
  onClose,
  onDeleted,
}: {
  brand: { id: number; name: string };
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const [reason, setReason] = useState('');
  const check = useDeletionCheck(brand.id);
  const deleteBrand = useDeleteBrand();

  const data = check.data;
  const count = data?.denominationCount ?? 0;
  const noun = `${count} denomination${count === 1 ? '' : 's'}`;

  const description = check.isLoading ? (
    <>Checking whether anything uses {brand.name}…</>
  ) : check.error || !data ? (
    <>Could not check whether {brand.name} is in use. Close this and try again.</>
  ) : data.canDeleteBrand ? (
    <>
      Nothing uses <strong>{brand.name}</strong> yet. It will be removed permanently
      {count > 0 ? (
        <>
          {' '}
          with its <strong>{noun}</strong>, their country offerings and provider mappings
        </>
      ) : null}
      , in every country. This can&apos;t be undone.
    </>
  ) : (
    <>
      <strong>{brand.name}</strong> can&apos;t be deleted: {data.usedDenominationCount} of its{' '}
      {noun} {data.usedDenominationCount === 1 ? 'is' : 'are'} used by orders, carts, payments or
      customers. Deactivate it instead, or delete the unused denominations one by one on its details
      page.
    </>
  );

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Delete this brand?"
      description={description}
      confirmLabel="Delete"
      variant="destructive"
      isPending={deleteBrand.isPending}
      confirmDisabled={!data?.canDeleteBrand}
      onConfirm={() => {
        const trimmed = reason.trim();
        deleteBrand.mutate(
          { brandId: brand.id, name: brand.name, reason: trimmed === '' ? null : trimmed },
          {
            onSuccess: () => {
              onClose();
              onDeleted?.();
            },
          },
        );
      }}
    >
      {data?.canDeleteBrand && (
        <ReasonField
          id="brand-delete-reason"
          value={reason}
          onChange={setReason}
          disabled={deleteBrand.isPending}
        />
      )}
    </ConfirmDialog>
  );
}

/**
 * Deleting one denomination the caller already knows is unused (from the
 * deletion check). Its country offerings and provider mappings go with it.
 */
export function DeleteDenominationDialog({
  brandId,
  denomination,
  onClose,
}: {
  brandId: number;
  denomination: { id: number; name: string };
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const deleteDenomination = useDeleteDenomination(brandId);

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Delete this denomination?"
      description={
        <>
          Nothing uses <strong>{denomination.name}</strong> yet. It will be removed permanently,
          with its country offerings and provider mappings, in every country. This can&apos;t be
          undone.
        </>
      }
      confirmLabel="Delete"
      variant="destructive"
      isPending={deleteDenomination.isPending}
      onConfirm={() => {
        const trimmed = reason.trim();
        deleteDenomination.mutate(
          {
            denominationId: denomination.id,
            name: denomination.name,
            reason: trimmed === '' ? null : trimmed,
          },
          // Closed on a refusal too: the toast explains it and the check refreshes.
          { onSettled: onClose },
        );
      }}
    >
      <ReasonField
        id="denomination-delete-reason"
        value={reason}
        onChange={setReason}
        disabled={deleteDenomination.isPending}
      />
    </ConfirmDialog>
  );
}

function ReasonField({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs">
        Reason{' '}
        <span className="text-muted-foreground font-normal">(optional, kept in the audit log)</span>
      </Label>
      <Input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={REASON_MAX}
        disabled={disabled}
        className="h-8 text-xs"
      />
    </div>
  );
}
